import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";
import type {
  ReviewImportResponse,
  ReviewImportStatus,
} from "@shopexperts/legacy-api";

export type ExpertIdSource = number | string | (() => number | string);

export interface ReviewImportInput {
  file: File;
  status?: ReviewImportStatus;
}

function resolveExpertId(source: ExpertIdSource): number | string {
  return typeof source === "function" ? source() : source;
}

export function useReviewImport(expertIdSource: ExpertIdSource) {
  const api = useApi();
  const queryClient = useQueryClient();
  const toaster = useToaster();

  const previewMutation = useMutation<ReviewImportResponse, Error, ReviewImportInput>({
    mutationFn: async ({ file, status }) => {
      return await api.admin.reviewsImport.previewImport({
        expertId: resolveExpertId(expertIdSource),
        file,
        status,
      });
    },
  });

  const importMutation = useMutation<ReviewImportResponse, Error, ReviewImportInput>({
    mutationFn: async ({ file, status }) => {
      return await api.admin.reviewsImport.runImport({
        expertId: resolveExpertId(expertIdSource),
        file,
        status,
      });
    },
    onSuccess: (response) => {
      const count = response.meta.imported_count ?? response.meta.row_count;
      const noun = count === 1 ? "review" : "reviews";
      const title =
        response.meta.status === "published"
          ? `Published ${count} ${noun} live`
          : `Queued ${count} ${noun} for moderation`;
      toaster.create({ title, type: "success" });
      queryClient.invalidateQueries({ queryKey: ["admin", "reviews"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "expert"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "experts"] });
    },
    onError: (error: Error) => {
      toaster.create({
        title: `Import failed: ${extractMessage(error)}`,
        type: "error",
      });
    },
  });

  function previewFile(file: File, status?: ReviewImportStatus) {
    return previewMutation.mutateAsync({ file, status });
  }

  function importFile(file: File, status?: ReviewImportStatus) {
    return importMutation.mutateAsync({ file, status });
  }

  function reset() {
    previewMutation.reset();
    importMutation.reset();
  }

  return {
    previewMutation,
    importMutation,
    previewFile,
    importFile,
    reset,
  };
}

export function extractMessage(error: unknown): string {
  if (!error) return "Unknown error";
  const anyErr = error as {
    response?: { data?: { message?: string; errors?: Record<string, string[]> } };
    message?: string;
  };
  const apiMessage = anyErr?.response?.data?.message;
  if (apiMessage) {
    const errors = anyErr.response?.data?.errors;
    if (errors) {
      const first = Object.values(errors)[0];
      if (Array.isArray(first) && first.length > 0) {
        return `${apiMessage} — ${first[0]}`;
      }
    }
    return apiMessage;
  }
  return anyErr?.message ?? "Unknown error";
}
